import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { LionFull } from '@/components/brand/Lion'
import { Button } from '@/components/ui/Button'
import { Field, Input } from '@/components/ui/Field'
import { ErrorState } from '@/components/ui/States'
import { supabase, describeError } from '@/lib/supabase'

/**
 * Sign in.
 *
 * Two routes in, both of which end at an account that already exists — public
 * sign-up is disabled on the Supabase project, so there is no third route and
 * no registration form to find:
 *
 *   1. Email and password, the daily path.
 *   2. A one-time link by email, for the day the password is on the other
 *      laptop. `shouldCreateUser: false` means an unknown address gets the same
 *      answer as a known one and learns nothing.
 *
 * Failures are deliberately vague about which half was wrong. "Those details
 * did not match" tells an attacker nothing about whether an address exists.
 */
export function SignInPage() {
  const [params] = useSearchParams()
  const [mode, setMode] = useState<'password' | 'link'>('password')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [linkSent, setLinkSent] = useState(false)

  const expired = params.get('reason') === 'expired'

  async function signInWithPassword(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)

    const { error: signInError } = await supabase.auth.signInWithPassword({ email: email.trim(), password })

    if (signInError) {
      setError(
        signInError.status === 400
          ? 'Those details did not match. Check the address and password, or use a sign-in link instead.'
          : describeError(signInError),
      )
      setBusy(false)
      return
    }

    // On success the auth listener swaps this page out; leave the button busy.
  }

  async function sendLink(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)

    const { error: linkError } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: {
        shouldCreateUser: false,
        emailRedirectTo: `${window.location.origin}/`,
      },
    })

    setBusy(false)

    // Shown whether or not the address exists. Never confirm an address.
    if (linkError && linkError.status !== 400) {
      setError(describeError(linkError))
      return
    }

    setLinkSent(true)
  }

  return (
    <main className="grid min-h-dvh place-items-center px-5 py-12">
      <div className="w-full max-w-[22rem]">
        <div className="mb-9 flex flex-col items-center text-center">
          <LionFull width={168} />
          <h1 className="mt-6 font-[family-name:var(--font-heading)] text-[0.9375rem] font-medium">
            De Leeuw Studio
          </h1>
          <p className="mt-2 text-[length:var(--text-sm)] text-taupe">Sign in to continue.</p>
        </div>

        {expired ? (
          <div className="mb-5 border-l-2 border-l-amber bg-[var(--color-amber-wash)] px-3.5 py-2.5 text-[length:var(--text-sm)]">
            Your session ended. Sign in again to pick up where you were.
          </div>
        ) : null}

        {error ? <ErrorState className="mb-5" title="Could not sign in" detail={error} /> : null}

        {linkSent ? (
          <div className="rounded-lg border px-4 py-5 text-center">
            <p className="text-[length:var(--text-sm)]">
              If <span className="font-medium">{email.trim()}</span> has an account, a sign-in link is on its way. It is
              good for one hour.
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="mt-3"
              onClick={() => {
                setLinkSent(false)
                setMode('password')
              }}
            >
              Back to sign in
            </Button>
          </div>
        ) : (
          <form onSubmit={mode === 'password' ? signInWithPassword : sendLink} className="flex flex-col gap-5">
            <Field label="Email">
              <Input
                type="email"
                autoComplete="username"
                autoCapitalize="off"
                spellCheck={false}
                required
                autoFocus
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>

            {mode === 'password' ? (
              <Field label="Password">
                <Input
                  type="password"
                  autoComplete="current-password"
                  required
                  minLength={12}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </Field>
            ) : null}

            <Button type="submit" variant="primary" loading={busy} loadingLabel={mode === 'password' ? 'Signing in' : 'Sending'} className="mt-1 w-full">
              {mode === 'password' ? 'Sign in' : 'Email me a link'}
            </Button>

            <button
              type="button"
              className="label mx-auto underline decoration-[var(--color-rule-strong)] underline-offset-4 hover:text-ink"
              onClick={() => {
                setMode(mode === 'password' ? 'link' : 'password')
                setError(null)
              }}
            >
              {mode === 'password' ? 'Use a sign-in link instead' : 'Use a password instead'}
            </button>
          </form>
        )}

        <p className="mt-10 text-center text-[length:var(--text-2xs)] text-taupe">
          Accounts are created by invitation only.
        </p>
      </div>
    </main>
  )
}
